/* eslint eslint-comments/no-use: off */

import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {useQuery} from '@github-ui/react-query'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {useCallback, useEffect, useMemo, useState} from 'react'

import {
  BranchPickerBase,
  type BranchPickerBaseProps,
  type BranchPickerBranch as BranchPickerBaseBranch,
} from './BranchPickerBase'
import {branchPickerSearchQueryKey, fetchBranchPickerSearch} from './fetch-branch-picker-search'

export type BranchPickerBranch = BranchPickerBaseBranch & {
  __typename?: 'Ref'
}

export type BranchPickerProps = Omit<BranchPickerBaseProps, 'filterItems'> & {
  repo: string
  owner: string
  branches: BranchPickerBranch[] | null
  activeBranch?: BranchPickerBranch | null
  enforceAtleastOneSelected?: boolean
}

export function BranchPicker({
  activeBranch,
  defaultBranchId,
  branches: inputBranches,
  owner,
  repo,
  onSelectionChange,
  title,
  anchorElement,
}: BranchPickerProps) {
  const [filter, setFilter] = useState('')
  const [searchResults, setSearchResults] = useState<BranchPickerBranch[] | undefined>(undefined)
  const [searchQuery, setSearchQuery] = useState('')
  const {addToast} = useToastContext()

  const branches = useMemo(() => {
    const repoBranches =
      (inputBranches || [])

        .filter(branch => Boolean(branch))
        .filter(branch => branch && branch.id !== activeBranch?.id) ?? []

    if (activeBranch) {
      repoBranches.unshift(activeBranch)
    }
    return repoBranches
  }, [inputBranches, activeBranch])

  const items = useMemo(() => {
    if (searchResults) {
      return searchResults
    }
    return branches
  }, [branches, searchResults])

  const query = useQuery({
    queryKey: branchPickerSearchQueryKey(owner, repo, searchQuery),
    queryFn: ({signal}) => fetchBranchPickerSearch(owner, repo, searchQuery, signal),
    enabled: searchQuery.length > 0,
  })

  useEffect(() => {
    if (!query.data) return

    const results = (query.data.repository?.refs?.edges || [])
      .map(edge => edge?.node ?? null)
      .map(branch => branch as BranchPickerBranch)
      .filter(branch => branch !== null)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSearchResults(results)
  }, [query.data])

  useEffect(() => {
    if (!query.isError || !query.errorUpdatedAt) return
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
    addToast({
      type: 'error',
      message: ERRORS.couldNotSearchBranches,
    })
  }, [addToast, query.errorUpdatedAt, query.isError])

  const fetchSearchData = useCallback((queryValue: string) => {
    if (!queryValue) {
      setSearchQuery('')
      setSearchResults(undefined)
      return
    }

    setSearchQuery(queryValue)
  }, [])

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  const filterItems = useCallback(
    (value: string) => {
      const trimmedFilter = value.trim()
      if (filter === trimmedFilter) return

      debounceFetchSearchData(trimmedFilter)
      setFilter(value)
    },
    [debounceFetchSearchData, filter],
  )

  return (
    <BranchPickerBase
      branches={items}
      filterItems={filterItems}
      activeBranch={activeBranch}
      defaultBranchId={defaultBranchId}
      onSelectionChange={onSelectionChange}
      isLoading={Boolean(searchQuery) && query.isFetching}
      anchorElement={anchorElement}
      title={title}
    />
  )
}
